import { useEffect, useState, useMemo, useCallback } from "react";
import moment from "moment";

import { useStaff } from "../../api/staffApi";
import Table from "../../components/Table";
import { useFilter, MultipleFilter } from "../../components/Filter";
import { usePagination } from "../../components/Pagination";
import {
  useStudentProgress,
  usePostStudentProgress,
} from "../../api/progressApi";
import { StudentsProgressTableDef } from "../../table/StudentsProgressTableDef";
import { getScoreXlsx } from "../../api/documentsApi";
import Dialog from "../../components/Dialog";
import { StatusSelectOptions, StudentProgress } from "../../types/user";
import { translator } from "../../utils/translator";
import { Checkbox, MenuItem, Select, TextField } from "@mui/material";
import { getProgressFilterDict } from "../../filters/progressFilter";

const EditScore = ({
  original,
  close,
  submit,
}: {
  original: StudentProgress;
  close: () => void;
  submit: (data: any) => Promise<void>;
}) => {
  const [originalProgress, setOriginalProgress] = useState<Record<string, any>>(
    {}
  );
  const [progress, setProgress] = useState<Record<string, any>>({});
  const { mutateAsync } = usePostStudentProgress();

  useEffect(() => {
    if (original) {
      setOriginalProgress(original.progress);
      setProgress(original.progress);
    }
  }, [original]);

  const saveProgress = useCallback(
    (key: string, type: "status" | "score_or_time", value: any) => {
      setProgress((prev) => ({
        ...prev,
        [key]: { ...prev[key], [type]: value },
      }));
    },
    []
  );

  const handleSubmit = async () => {
    if (
      progress.attitude.score_or_time &&
      (progress.attitude.score_or_time < 0 ||
        progress.attitude.score_or_time > 100)
    ) {
      alert("연수 태도 점수는 0~100 사이여야 합니다.");
      return;
    }

    if (
      progress.homework_score.score_or_time &&
      (progress.homework_score.score_or_time < 0 ||
        progress.homework_score.score_or_time > 100)
    ) {
      alert("과제 점수는 0~100 사이여야 합니다.");
      return;
    }

    if (
      progress.training_score.score_or_time &&
      (progress.training_score.score_or_time < 0 ||
        progress.training_score.score_or_time > 100)
    ) {
      alert("실습 점수는 0~100 사이여야 합니다.");
      return;
    }

    const data = Object.fromEntries(
      (Object.entries(progress) as [keyof StudentProgress, any][]).filter(
        ([key, value]) => value !== originalProgress[key]
      )
    );
    data.id = original.id;
    await mutateAsync(data);

    try {
      await submit(data);
      alert("성공적으로 등록했습니다.");
      close();
    } catch (e) {
      console.error(e);
      alert("수정 중 오류가 발생했습니다.");
    }
  };

  return (
    <Dialog
      title="점수 입력"
      buttons={["close", "ok"]}
      ok={async () => await handleSubmit()}
      close={close}
    >
      <div className="binary-grid" style={{ width: "800px" }}>
        <>
          <p>이름</p>
          <p>{original.name}</p>
        </>
        <>
          <p>구분</p>
          <p>{translator[original.department]}</p>
        </>
        <>
          <p>과정</p>
          <p>{translator[original.course]}</p>
        </>
        <>
          <p>학생코드</p>
          <p>{original.user_code}</p>
        </>
        <>
          <p>{translator.progress_attitude}</p>
          <div>
            <TextField
              type="number"
              variant="standard"
              value={progress.attitude?.score_or_time || ""}
              placeholder="태도 점수"
              onChange={(e) =>
                saveProgress(
                  "attitude",
                  "score_or_time",
                  e.target.value.slice(0, 3)
                )
              }
            />
            <p>점</p>
          </div>
        </>
        <>
          <p>{translator.progress_homework_score}</p>
          <div>
            <TextField
              type="number"
              variant="standard"
              value={progress.homework_score?.score_or_time || ""}
              placeholder="과제 점수"
              onChange={(e) =>
                saveProgress(
                  "homework_score",
                  "score_or_time",
                  e.target.value.slice(0, 3)
                )
              }
            />
            <p>점</p>
          </div>
        </>
        <>
          <p>{translator.progress_e_learning}</p>
          <div>
            <TextField
              type="number"
              variant="standard"
              value={progress.e_learning?.score_or_time || ""}
              placeholder="이론 연수 시간"
              onChange={(e) =>
                saveProgress("e_learning", "score_or_time", e.target.value)
              }
            />
            <p>
              분 ({Math.floor(progress.e_learning?.score_or_time / 60)}시간{" "}
              {progress.e_learning?.score_or_time % 60}분)
            </p>
          </div>
        </>
        <>
          <p>{translator.progress_training_score}</p>
          <div>
            <TextField
              type="number"
              variant="standard"
              value={progress.training_score?.score_or_time || ""}
              placeholder="실습 점수"
              onChange={(e) =>
                saveProgress(
                  "training_score",
                  "score_or_time",
                  e.target.value.slice(0, 3)
                )
              }
            />
            <p>점</p>
          </div>
        </>
        <>
          <p>{translator.progress_training_time}</p>
          <div>
            <TextField
              type="number"
              variant="standard"
              value={progress.training_time?.score_or_time || ""}
              placeholder="실습 시간"
              onChange={(e) =>
                saveProgress("training_time", "score_or_time", e.target.value)
              }
            />
            <p>
              분 ({Math.floor(progress.training_time?.score_or_time / 60)}시간{" "}
              {progress.training_time?.score_or_time % 60}분)
            </p>
          </div>
        </>
        <>
          <p>{translator.progress_homework}</p>
          <div>
            <Select
              variant="standard"
              value={progress.homework?.status || "empty"}
              onChange={(e) =>
                saveProgress(
                  "homework",
                  "status",
                  e.target.value === "empty" ? "" : e.target.value
                )
              }
            >
              {StatusSelectOptions.map((option) => (
                <MenuItem value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
            <p>{translator[progress.homework?.status || "empty"]}</p>
          </div>
        </>
        <>
          <p>{translator.progress_training}</p>
          <div>
            <Select
              variant="standard"
              value={progress.training?.status || "empty"}
              onChange={(e) =>
                saveProgress(
                  "training",
                  "status",
                  e.target.value === "empty" ? "" : e.target.value
                )
              }
            >
              {StatusSelectOptions.map((option) => (
                <MenuItem value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
            <p>{translator[progress.training?.status || "empty"]}</p>
          </div>
        </>
        <>
          <p>{translator.progress_docu_post}</p>
          <div>
            <Checkbox
              checked={progress.docu_post?.status === "True"}
              onChange={(e) => {
                saveProgress(
                  "docu_post",
                  "status",
                  e.target.checked ? "True" : ""
                );
              }}
            />
          </div>
        </>
      </div>
    </Dialog>
  );
};

const ScoreUpdate = () => {
  const { data: staff } = useStaff();
  const { pagination, setPage } = usePagination();
  const [openDialog, setOpenDialog] = useState<number>();
  const [filters, setFilters] = useState({});
  const { valueDict, setValue } = useFilter();

  const { data, isLoading: isProgressLoading } = useStudentProgress({
    filters,
    pagination,
  });
  const progressFields = [
    "attitude",
    "homework_score",
    "e_learning",
    "training_score",
    "training_time",
    "homework",
    "training",
    "docu_post",
  ];
  const [studentsProgress, setStudentsProgress] = useState<any[]>();
  const [isCreating, setIsCreating] = useState(false);

  const maxPage = useMemo(() => {
    if (!data) return 0;
    return Math.ceil(data.total / 10);
  }, [data]);

  const handleClick = useCallback(async () => {
    setIsCreating(true);
    try {
      const result = await getScoreXlsx();
      const url = window.URL.createObjectURL(new Blob([result.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `score_${moment().format("YYYYMMDD")}.xlsx`;
      a.click();
    } catch (e) {
      console.error(e);
    } finally {
      setIsCreating(false);
    }
  }, []);

  useEffect(() => {
    if (data) {
      setStudentsProgress(data.students);
    }
  }, [data]);

  return (
    <div className="vertical center fullWidth" id="docsCheckWrapper">
      <div className="search">
        <MultipleFilter
          filterDict={getProgressFilterDict(
            staff ? staff.staff.department : ["life", "pro"],
            ""
          )}
          valueDict={valueDict}
          setValue={setValue}
          columnCount={3}
        />
        <button
          onClick={() => {
            setPage(0);
            setFilters(valueDict);
          }}
        >
          검색
        </button>
      </div>
      {isProgressLoading && <div>로딩중...</div>}
      {studentsProgress && progressFields && (
        <>
          <button
            style={{
              backgroundColor: "var(--primary-500)",
              color: "white",
              width: "fit-content",
              marginBottom: "10px",
            }}
            onClick={handleClick}
            disabled={isCreating}
          >
            {isCreating ? "downloading...." : "점수 엑셀파일"}
          </button>
          <div className="docsCheck">
            <Table
              columns={StudentsProgressTableDef({
                onClick: ({ studentId }) => setOpenDialog(studentId),
                progressFields,
              })}
              data={studentsProgress || []}
              paginationProps={{ page: pagination.page, setPage, maxPage }}
              columnPinning={{
                left: ["user_code"],
              }}
            />
          </div>
        </>
      )}
      {openDialog && (
        <EditScore
          original={studentsProgress?.find((s) => s.id === openDialog)}
          close={() => setOpenDialog(undefined)}
          submit={async (data) => {}}
        />
      )}
    </div>
  );
};

export default ScoreUpdate;
